import { DeliveryLocationTypeModel } from '@/http/models/DeliveryLocationType'
import { toast } from 'sonner'

import { useState } from 'react'

import { DeliveryLocationTypeModel as DeliveryLocationTypeType } from '@/types'

import { useFetchDeliveryLocationTypes } from './useFetchDeliveryLocationTypes'

type DialogMode = 'create' | 'edit' | 'view'

export const useDeliveryLocationTypesTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [deliveryLocationTypeDialogOpen, setDeliveryLocationTypeDialogOpen] = useState(false)
  const [editDeliveryLocationType, setEditDeliveryLocationType] =
    useState<DeliveryLocationTypeType.DeliveryLocationType | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deliveryLocationTypeToDelete, setDeliveryLocationTypeToDelete] =
    useState<DeliveryLocationTypeType.DeliveryLocationType | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchDeliveryLocationTypes({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditDeliveryLocationType(null)
    setDeliveryLocationTypeDialogOpen(true)
  }

  const openViewDialog = (item: DeliveryLocationTypeType.DeliveryLocationType) => {
    setMode('view')
    setEditDeliveryLocationType(item)
    setDeliveryLocationTypeDialogOpen(true)
  }

  const openEditDialog = (item: DeliveryLocationTypeType.DeliveryLocationType) => {
    setMode('edit')
    setEditDeliveryLocationType(item)
    setDeliveryLocationTypeDialogOpen(true)
  }

  const closeDialog = () => {
    setDeliveryLocationTypeDialogOpen(false)
    setEditDeliveryLocationType(null)
  }

  const handleDeleteClick = (item: DeliveryLocationTypeType.DeliveryLocationType) => {
    setDeliveryLocationTypeToDelete(item)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deliveryLocationTypeToDelete?._id) return

    setDeleteLoading(true)
    const response = await DeliveryLocationTypeModel.delete({
      params: { id: deliveryLocationTypeToDelete._id },
    })

    if (response.success) {
      toast.success('Delivery location type deleted successfully')
      setDeleteDialogOpen(false)
      setDeliveryLocationTypeToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete delivery location type')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (item: DeliveryLocationTypeType.DeliveryLocationType) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active'

    const response = await DeliveryLocationTypeModel.update({
      params: { id: item._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Status updated successfully')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateDeliveryLocationType = async (data: any) => {
    setCreateLoading(true)
    const response = await DeliveryLocationTypeModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Delivery location type created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create delivery location type')
    }
    setCreateLoading(false)
  }

  const handleUpdateDeliveryLocationType = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await DeliveryLocationTypeModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Delivery location type updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update delivery location type')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalTypes: pagination.totalCount,
    activeTypes: data.filter((item) => item.status === 'active').length,
    inactiveTypes: data.filter((item) => item.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,
    mode,
    deliveryLocationTypeDialogOpen,
    editDeliveryLocationType,
    deleteDialogOpen,
    deliveryLocationTypeToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateDeliveryLocationType,
    handleUpdateDeliveryLocationType,
    toggleStatus,
  }
}
